import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PortfolioBalanceIcon } from '../../../../assets/icons';
import { Button, Dropdown } from '../../../../components';
import CustomInput from '../../../../components/CustomInput';
import { DisplayField, DisplayFieldType, DisplaySection } from '../../../../components/DisplayForms';
import DisplayForm from '../../../../components/DisplayForms/DisplayForm';
import { DropdownItem } from '../../../../components/Dropdown';
import { PortfolioBalance } from '../../../../types/DomainTypes';
import { buildDisplayText, formatterGbp } from '../../../../utils';
import { usePortfolioBalances } from '../../../Portfolio/components/Summary/hooks/usePortfolioBalances';
import { ModelKeys, ModelType, TopupSlideoutViewState, TopupSlideoutViewType } from '../../types';

const getFieldAttributes = (modelKey: string, label = '') => ({
  id: modelKey,
  name: modelKey,
  ariaLabel: modelKey,
  modelKey,
  className: 'mb-5 ',
  containerClassName: 'mb-8 gap-5 ',
  translationKey: ``,
  placeholder: '',

  onBlur: () => {},
  label: {
    text: label,
    isShow: label.length > 0,
    className: 'mb-2 block',
  },
});

enum DisplayTextKeys {
  TOPUP_AMOUNT_TEXT = 'top_up_amount',
  PORTFOLIO_PLACEHOLDER_TEXT = 'portfolio_placeholder_text',
  PAYMENT_PLACEHOLDER_TEXT = 'payment_placeholder_text',
  SELECT_PORTFOLIO_TITLE = 'select_portfolio_title',
  PAYMENT_FREQUENCY_TITLE = 'payment_frequency_title',
  PAYMENT_METHOD_TITLE = 'payment_method_title',
  TOPUP_TEXT = 'account:text.topup',
  NEXT = 'next',
  RECURRING_PAYMENT_TEXT = 'recurring_payment_text',
  FREQUENCY_TEXT = 'frequency_text',
  START_DATE_TEXT = 'start_date_text',
  CONTACT_US = 'common:contact',
}

interface TopupSlideoutProps {
  timestamp?: number;
  onClose?: () => void;
  onStateChange?: (state: TopupSlideoutViewState) => void;
  initialModel: ModelType;
}

const OTPTopup = forwardRef(({ onStateChange, initialModel }: TopupSlideoutProps, ref) => {
  const { t } = useTranslation();
  const { checkoutOtp } = useFlags();

  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:slideout.topup', t), [t]);
  const [portfolioText, setPortfolioText] = useState('');

  const [model, setModel] = useState<ModelType>(initialModel);

  const { portfolioBalances } = usePortfolioBalances();

  const portfolioDropdownOptions = useMemo(() => {
    return portfolioBalances
      .filter(
        (x: PortfolioBalance) => `${x.portfolioId}`.length > 0 && !!x.currentFeeModel && x.currentFeeModel === true,
      )
      .map((balance: PortfolioBalance) => {
        const { portfolioId, portfolioName } = balance;
        return {
          id: portfolioId,
          value: portfolioId,
          content: (
            <div className="flex justify-between text-base text-gray-800 dark:text-gray-200">
              <span>{portfolioName}</span>
            </div>
          ),
        };
      });
  }, [portfolioBalances]);

  const onPortfolioChange = (item: DropdownItem) => {
    const selectedPortfolio = portfolioBalances.find((x) => x.portfolioId === item.value);
    setPortfolioText((selectedPortfolio || { portfolioName: '' }).portfolioName);
    setModel({ ...model, [ModelKeys.PORTFOLIO_ID]: `${selectedPortfolio?.portfolioId}` });
  };

  const sections: DisplaySection[] = useMemo(() => {
    const ddlConfig = {
      itemWrapperStyle: { width: '100%' },
      containerClassName: 'w-full',
      itemsContainerClassName: 'h-[300px] overflow-y-auto w-full bg-white dark:bg-slate-700',
      itemClassName: 'py-5 text-base flex text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600',
      className:
        'flex-1 text-sm sm:text-14 whitespace-nowrap p-0 justify-start border-b border-b-gray-400 dark:border-b-slate-500 text-gray-800 dark:text-gray-200',
    };

    const fields: DisplayField[] = [
      {
        ...getFieldAttributes(ModelKeys.PORTFOLIO_ID, displayText[DisplayTextKeys.SELECT_PORTFOLIO_TITLE]),
        type: DisplayFieldType.CUSTOM,
        customTemplate: () => {
          return (
            <Dropdown
              placeholder={displayText[DisplayTextKeys.PORTFOLIO_PLACEHOLDER_TEXT]}
              value={model.portfolioId}
              valueTemplate={
                <div className="flex w-[95%]">
                  <span className="truncate block">{portfolioText}</span>
                </div>
              }
              onItemSelect={onPortfolioChange}
              items={portfolioDropdownOptions}
              {...ddlConfig}
            />
          );
        },
      },
    ];

    return [
      {
        className: 'gap-5  w-full',
        fields,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const onNext = () => {
    if (onStateChange)
      onStateChange({
        showBackButton: true,
        view: TopupSlideoutViewType.CHECKOUT_OTP_PAYMENT,
        model,
      });
  };

  const onBack = () => {
    let showBackButton = true;
    const viewUpdate = TopupSlideoutViewType.NONE;
    showBackButton = false;

    if (onStateChange)
      onStateChange({
        showBackButton,
        view: viewUpdate,
      });
  };

  useImperativeHandle(ref, () => ({
    onBack,
  }));

  useEffect(() => {
    if (portfolioDropdownOptions) {
      const pId = model[ModelKeys.PORTFOLIO_ID];
      if (portfolioDropdownOptions.length === 1) {
        onPortfolioChange(portfolioDropdownOptions[0]);
      } else if (portfolioDropdownOptions.length > 0 && pId && pId.length > 0) {
        onPortfolioChange(portfolioDropdownOptions.find((x) => `${x.id}` === `${pId}`)!);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioDropdownOptions]);

  const isFormValid = () => {
    return (model[ModelKeys.AMOUNT] as number) > 0 && model[ModelKeys.PORTFOLIO_ID].length > 0;
  };

  const disableButton = !isFormValid();
  const viewTitle = displayText[DisplayTextKeys.TOPUP_AMOUNT_TEXT];

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-900">
      {/* Amount Input Section */}
      <div className="flex items-center flex-col py-8 px-5 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
        <div className="w-14 h-14 rounded-full bg-orange/10 dark:bg-teal-500/10 flex items-center justify-center mb-3">
          <PortfolioBalanceIcon className="w-7 h-7 text-orange dark:text-teal-400" />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 mb-2">{viewTitle}</span>

        <CustomInput
          inputProps={{
            inputContainerClassName: 'border-none bg-transparent',
          }}
          value={Number(model.amount) < 0 ? '' : model.amount}
          type={DisplayFieldType.CURRENCY}
          name={DisplayTextKeys.TOPUP_AMOUNT_TEXT}
          placeholder={formatterGbp.format(0)}
          inputClassName="text-center text-3xl font-semibold border-none text-gray-900 dark:text-white bg-transparent"
          onChange={(
            e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
            value?: string | number,
          ) => {
            setModel({ ...model, [ModelKeys.AMOUNT as string]: value });
          }}
        />
      </div>

      {/* Portfolio Selection */}
      <div className="flex flex-1 flex-col h-full p-5 overflow-x-hidden overflow-y-auto">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-slate-700">
          <DisplayForm sections={sections} model={{ ...model, modelType: 'TopUp' }} />
        </div>
      </div>

      {/* Action Button */}
      <div className="p-5 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700">
        <Button
          isDisable={disableButton}
          className={`w-full py-3 rounded-xl font-medium transition-all duration-200 ${
            disableButton
              ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
              : 'bg-orange dark:bg-teal-500 text-white hover:bg-orange/90 dark:hover:bg-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25'
          }`}
          onClick={onNext}
          props={{
            name: DisplayTextKeys.NEXT,
          }}
        >
          {displayText[DisplayTextKeys.NEXT]}
        </Button>
      </div>
    </div>
  );
});

export default OTPTopup;
